import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  resendSignUpCode,
} from 'aws-amplify/auth';
import { setTokens, clearTokens } from '@/services/api/client';

interface User {
  id: string;
  email: string;
  name?: string;
  emailVerified?: boolean;
  isSiteAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSiteAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ needsVerification: boolean }>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthState = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      if (session.tokens?.accessToken && session.tokens?.idToken) {
        // Store ID token for API client (contains user attributes like name, email)
        const idToken = session.tokens.idToken.toString();
        // Note: Amplify v6 doesn't expose refresh token directly, use empty string
        const refreshToken = '';
        setTokens(idToken, refreshToken);

        const payload = session.tokens.idToken.payload;
        
        // Extract site admin status from custom Cognito attribute
        // The attribute is stored as 'custom:is_site_admin' with value 'true' or 'false'
        const isSiteAdmin = payload?.['custom:is_site_admin'] === 'true';
        
        setUser({
          id: currentUser.userId,
          email: (payload?.email as string) || currentUser.signInDetails?.loginId || '',
          name: payload?.name as string | undefined,
          emailVerified: payload?.email_verified as boolean | undefined,
          isSiteAdmin,
        });
      }
    } catch {
      setUser(null);
      clearTokens();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial auth check on mount syncs with Amplify (external system)
    checkAuthState();
  }, [checkAuthState]);

  const handleSignIn = useCallback(
    async (email: string, password: string): Promise<{ needsVerification: boolean }> => {
      const result = await amplifySignIn({ username: email, password });

      if (result.isSignedIn) {
        await checkAuthState();
        return { needsVerification: false };
      }

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        return { needsVerification: true };
      }

      throw new Error('Sign in failed');
    },
    [checkAuthState]
  );

  const handleSignUp = useCallback(async (email: string, password: string, name: string) => {
    await amplifySignUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          name,
        },
      },
    });
  }, []);

  const handleConfirmSignUp = useCallback(async (email: string, code: string) => {
    await amplifyConfirmSignUp({
      username: email,
      confirmationCode: code,
    });
  }, []);

  const handleResendVerificationCode = useCallback(async (email: string) => {
    await resendSignUpCode({ username: email });
  }, []);

  const handleSignOut = useCallback(async () => {
    await amplifySignOut();
    setUser(null);
    clearTokens();
  }, []);

  const handleResetPassword = useCallback(async (email: string) => {
    await amplifyResetPassword({ username: email });
  }, []);

  const handleConfirmResetPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      await amplifyConfirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
    },
    []
  );

  const refreshSession = useCallback(async () => {
    await checkAuthState();
  }, [checkAuthState]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isSiteAdmin: user?.isSiteAdmin ?? false,
        signIn: handleSignIn,
        signUp: handleSignUp,
        confirmSignUp: handleConfirmSignUp,
        resendVerificationCode: handleResendVerificationCode,
        signOut: handleSignOut,
        resetPassword: handleResetPassword,
        confirmResetPassword: handleConfirmResetPassword,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
